<template>
	<ui-modal-template>
		<template v-slot:title>
			{{ title }}
		</template>

		<template v-slot:content>
			<ui-form class="h-full flex flex-col" :loading="loading" @submit="submitForm">
				<div class="p-0">

					<!-- Datum + Preisquelle -->
					<div class="flex flex-col md:flex-row px-6 gap-x-6">
						<ui-input-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.date"
							field="date"
							label="Datum"
							type="date"
							:form="form"
							:required="true"
						/>

						<ui-select-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.price_source_id"
							field="price_source_id"
							label="Preisquelle"
							:options="priceSources"
							:form="form"
							:required="false"
						/>
					</div>

					<hr class="h-0.5 bg-gray-200 text-gray-200 mt-4" />

					<!-- Produkt -->
					<div class="flex flex-col md:flex-row px-6 gap-x-6">
						<ui-select-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.product_grade_id"
							field="product_grade_id"
							label="Güteklasse"
							:options="gradeOptions"
							:form="form"
							:required="true"
							:placeholder="false"
						/>

						<ui-select-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.product_harvest"
							field="product_harvest"
							label="Erntejahr"
							:options="harvestYears"
							:form="form"
							:required="true"
							:placeholder="false"
						/>
					</div>

					<hr class="h-0.5 bg-gray-200 text-gray-200 mt-4" />

					<!-- Preis/Basis Modus -->
					<div class="flex items-center gap-x-6 px-6 pt-4">
						<ui-radio-group
							v-for="mode in inputModes"
							:key="mode.id"
							v-model="form.input_mode"
							field="input_mode"
							:form="form"
							name="inputMode"
							:radio-value="mode.id"
							:label="mode.name"
						/>
					</div>

					<!-- Future + Handelsmonat + Matif -->
					<div class="flex flex-col md:flex-row px-6 gap-x-6">
						<ui-select-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.commodity_future_id"
							field="commodity_future_id"
							label="Future"
							:options="futuresOptions"
							:form="form"
							:required="true"
						:placeholder="false"
						/>

						<ui-input-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.matif_kurs"
							field="matif_kurs"
							label="Matif"
							suffix="€"
							:dp="2"
							:form="form"
							:required="true"
							@input.native="onNumericInput($event, 'matif_kurs')"
						/>
					</div>

					<!-- Preis + Basis -->
					<div class="flex flex-col md:flex-row px-6 gap-x-6">
						<ui-input-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.price_mt"
							field="price_mt"
							label="Flatpreis"
							suffix="€"
							:dp="2"
							:form="form"
							:required="isPriceInput(form.input_mode)"
							:disabled="isBasisInput(form.input_mode)"
							@input.native="onNumericInput($event, 'price_mt')"
						/>

						<ui-input-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.price_basis"
							field="price_basis"
							label="Basis"
							suffix="€"
							:dp="2"
							:form="form"
							:required="isBasisInput(form.input_mode)"
							:disabled="isPriceInput(form.input_mode)"
							@input.native="onNumericInput($event, 'price_basis')"
						/>

						<ui-input-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.price_margin"
							field="price_margin"
							label="Marge"
							suffix="€"
							:dp="2"
							:form="form"
							:required="false"
							@input.native="onNumericInput($event, 'price_margin')"
						/>
					</div>

					<hr class="h-0.5 bg-gray-200 text-gray-200 mt-4" />

					<div class="flex flex-col md:flex-row px-6 gap-x-6">
						<ui-select-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.location_mode"
							field="location_mode"
							label="Parität"
							:options="locationModes"
							:form="form"
							:required="true"
							:placeholder="false"
						/>

						<ui-select-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.market_location_id"
							field="market_location_id"
							label="Marktstandort"
							:options="marketLocationOptions"
							:form="form"
							:required="true"
							:placeholder="false"
						/>

						<ui-select-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.date_type"
							field="date_type"
							label="Termin"
							:options="dateTypes"
							:form="form"
							:required="true"
							:placeholder="false"
						/>
					</div>

					<div v-if="selectedDateType && selectedDateType.date_month_from" class="flex flex-col md:flex-row px-6 gap-x-6">
						<ui-month-input-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.date_month_from"
							field="date_month_from"
							label="Von"
							:form="form"
							:required="true"
							:min-date="monthMinDate"
							vertical-alignment="bottom"
						/>

						<ui-month-input-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.date_month_to"
							field="date_month_to"
							label="Bis"
							:form="form"
							:required="true"
							:min-date="monthMinDate"
							vertical-alignment="bottom"
						/>
					</div>

					<div v-if="selectedDateType && selectedDateType.date_month_to && !selectedDateType.date_month_from" class="flex flex-col md:flex-row px-6 gap-x-6">
						<ui-month-input-group
							class="basis-full sm:basis-[30.75%] py-4"
							v-model="form.date_month_to"
							field="date_month_to"
							label="Bis"
							:form="form"
							:required="true"
							:min-date="monthMinDate"
							vertical-alignment="bottom"
						/>
					</div>

				</div>

				<div class="bg-gray-100 p-6 px-6 mt-4 rounded-b-2xl">
					<div class="flex justify-end">
						<ui-button
							:disabled="loading"
							native-type="submit"
							label="Speichern"
						/>
					</div>
				</div>
			</ui-form>
		</template>
	</ui-modal-template>
</template>

<script>
import Vue from "vue";

export default {
	props: {
		productTypes: {
			type: Array,
			required: true,
		},
		productGrades: {
			type: Array,
			required: true,
		},
		dateTypes: {
			type: Array,
			required: true,
		},
		locationModes: {
			type: Array,
			required: true,
		},
		harvestYears: {
			type: Array,
			required: true,
		},
		currentHarvestYear: {
			type: Number,
			required: true,
		},
		marketLocations: {
			type: Array,
			required: true,
		},
		priceSources: {
			type: Array,
			required: true,
		},
		futures: {
			type: Array,
			default: () => [],
		},
		inputModes: {
			type: Array,
			required: true,
		},
		defaultInputMode: {
			type: String,
			required: true,
		},
		row: {
			type: Object,
			default: null,
		},
		initialValues: {
			type: Object,
			default: null,
		},
	},
	data() {
		const v      = this.row ?? this.initialValues;
		const isCopy = !this.row && !!this.initialValues;

		return {
			loading: false,
			recalcTimer: null,
			pendingRecalc: null,
			form: Vue.observable({
				errors: {},
				hasErrors: false,
				clearErrors() {},
				// Bearbeiten/Kopieren übernimmt den gespeicherten Modus (NULL = unbekannt → keine Vorauswahl).
				input_mode: v ? v.input_mode ?? null : this.defaultInputMode,
				date: v?.date ?? new Date().toISOString().slice(0, 10),
				product_grade_id: v?.product_grade_id ?? null,
				product_harvest: v?.product_harvest ?? this.currentHarvestYear,
				commodity_future_id: isCopy ? null : v?.commodity_future_id ?? null,
				price_mt: isCopy ? null : v?.price_mt ?? null,
				price_basis: isCopy ? null : v?.price_basis ?? null,
				price_margin: isCopy ? null : v?.price_margin ?? null,
				matif_kurs: isCopy ? null : v?.matif_kurs ?? null,
				location_mode: v?.location_mode ?? null,
				market_location_id: v?.market_location_id ?? null,
				price_source_id: v?.price_source_id ?? null,
				date_type: v?.date_type ?? null,
				date_month_from: v?.date_month_from ?? null,
				date_month_to: v?.date_month_to ?? null,
			}),
		};
	},
	computed: {
		/**
		 * @return {string}
		 */
		title() {
			if (this.row) {
				return this.__("Edit price");
			}

			if (this.initialValues) {
				return this.__("Copy price");
			}

			return this.__("Add price");
		},

		gradeOptions() {
			return this.productGrades.map((g) => {
				const raw              = g.name.replace("product.grades.long.", "");
				const [grade, protein] = raw.split("__");
				const label            = this.__("product.grades.default." + grade);

				return {
					id:   g.id,
					name: protein ? `${label} (${this.__("product.proteins." + protein)})` : label,
				};
			});
		},

		marketLocationOptions() {
			return this.marketLocations.map((l) => ({
				id: l.id,
				name: l.label,
			}));
		},

		selectedDateType() {
			return this.dateTypes.find((t) => t.id === this.form.date_type) ?? null;
		},

		futuresOptions() {
			const grade = this.productGrades.find((g) => g.id === this.form.product_grade_id);

			if (!grade) return [];

			const ticker = this.productTypes.find((t) => t.id === grade.product_type_id)?.ticker_code;

			return (this.futures || [])
				.filter((f) => f.ticker_code === ticker)
				.map((f) => ({ id: f.id, name: f.name }));
		},

		/**
		 * Frühester wählbarer Liefermonat: der Monat des Eingabedatums (form.date). Damit lassen
		 * sich keine Monate aus der Vergangenheit relativ zum Preis-Datum wählen — beim Bearbeiten
		 * eines Preises aus einem früheren Monat bleibt dessen Monat gültig.
		 *
		 * @return {string} ISO "YYYY-MM-01"
		 */
		monthMinDate() {
			const date = this.form.date || new Date().toISOString().slice(0, 10);

			return date.slice(0, 7) + "-01";
		},
	},
	watch: {
		'form.date_type'() {
			this.form.date_month_from = null;
			this.form.date_month_to   = null;
		},
		'form.product_grade_id'() {
			this.form.commodity_future_id = null;
		},
		'form.commodity_future_id'(id) {
			const future = (this.futures || []).find((f) => f.id === id);

			if (!future) return;

			this.form.matif_kurs = future.euronext_price;
			this.recalculate();
		},
		'form.input_mode'() {
			this.recalculate();
			this.$delete(this.form.errors, 'price_mt');
			this.$delete(this.form.errors, 'price_basis');
		},
	},

	methods: {
		/**
		 * @param {string|null} modeId
		 * @return {boolean}
		 */
		isPriceInput(modeId) {
			return this.inputModes.find((m) => m.id === modeId)?.is_price_input === true;
		},

		/**
		 * @param {string|null} modeId
		 * @return {boolean}
		 */
		isBasisInput(modeId) {
			return this.inputModes.find((m) => m.id === modeId)?.is_basis_input === true;
		},

		/**
		 * Live-Recalc bei Eingabe (debounced). Liest den getippten Wert direkt aus dem Event,
		 * da das ui-input-group den v-model erst bei Blur committet.
		 *
		 * @param {InputEvent} event
		 * @param {string} field
		 * @return {void}
		 */
		onNumericInput(event, field) {
			this.pendingRecalc = { field, value: event.target.value };

			clearTimeout(this.recalcTimer);
			this.recalcTimer = setTimeout(() => this.flushRecalc(), 250);
		},

		/**
		 * Führt eine ausstehende Live-Berechnung sofort aus (z.B. vor dem Absenden).
		 *
		 * @return {void}
		 */
		flushRecalc() {
			clearTimeout(this.recalcTimer);

			if (!this.pendingRecalc) return;

			const { field, value } = this.pendingRecalc;
			this.pendingRecalc = null;
			this.recalculateLive(field, value);
		},

		/**
		 * Berechnet das abgeleitete Feld aus dem live getippten Wert. Marge wirkt nur auf den
		 * Preis; Basis bleibt unberührt → bei Margenänderung den Preis neu rechnen.
		 *
		 * @param {string} field - geändertes Feld
		 * @param {string} value - live getippter Wert dieses Feldes
		 * @return {void}
		 */
		recalculateLive(field, value) {
			const parse  = (v) => parseFloat(('' + (v ?? '')).replace(',', '.'));
			const matif  = field === 'matif_kurs' ? parse(value) : parse(this.form.matif_kurs);
			const margin = (field === 'price_margin' ? parse(value) : parse(this.form.price_margin)) || 0;

			if (field === 'price_margin') {
				const priceBasis = parse(this.form.price_basis);
				this.form.price_mt = (!isNaN(matif) && !isNaN(priceBasis)) ? (matif + priceBasis + margin).toFixed(2) : this.form.price_mt;

				return;
			}

			if (this.isPriceInput(this.form.input_mode)) {
				const priceMt = field === 'price_mt' ? parse(value) : parse(this.form.price_mt);
				this.form.price_basis = (!isNaN(matif) && !isNaN(priceMt)) ? (priceMt - margin - matif).toFixed(2) : this.form.price_basis;
			} else {
				const priceBasis = field === 'price_basis' ? parse(value) : parse(this.form.price_basis);
				this.form.price_mt = (!isNaN(matif) && !isNaN(priceBasis)) ? (matif + priceBasis + margin).toFixed(2) : this.form.price_mt;
			}
		},

		/**
		 * @return {void}
		 */
		recalculate() {
			if (this.isPriceInput(this.form.input_mode)) {
				this.recalculateBasis();
			} else {
				this.recalculatePrice();
			}
		},

		/**
		 * @return {void}
		 */
		recalculateBasis() {
			const matif   = parseFloat(this.form.matif_kurs);
			const margin  = parseFloat(this.form.price_margin) || 0;
			const priceMt = parseFloat(this.form.price_mt);

			if (!isNaN(matif) && !isNaN(priceMt)) {
				this.form.price_basis = (priceMt - margin - matif).toFixed(2);
			}
		},

		/**
		 * @return {void}
		 */
		recalculatePrice() {
			const matif      = parseFloat(this.form.matif_kurs);
			const margin     = parseFloat(this.form.price_margin) || 0;
			const priceBasis = parseFloat(this.form.price_basis);

			if (!isNaN(matif) && !isNaN(priceBasis)) {
				this.form.price_mt = (matif + priceBasis + margin).toFixed(2);
			}
		},

		/**
		 * @return {void}
		 */
		async submitForm() {
			this.flushRecalc();
			this.loading = true;

			try {
				if (this.row) {
					await axios.patch(
						this.$route("admin.prices.update", { id: this.row.id }),
						this.form
					);
				} else {
					await axios.post(this.$route("admin.prices.store"), this.form);
				}

				this.$bus.$emit("modal:close");
				this.$bus.$emit("content:load");
			} catch (error) {
				if (error.response?.status === 422) {
					this.$bus.$emit("form:invalid", error.response.data.errors);
				} else {
					this.$bus.$emit("modal:error", error);
				}
			} finally {
				this.loading = false;
			}
		},
	},
};
</script>
